import React, { Children } from 'react';
import classNames from 'classnames/bind';
import { lruMemoize } from 'reselect';
import styles from './stack.module.pcss';

type Align = 'fill' | 'left' | 'right';
type AlignVertical = 'top' | 'center' | 'bottom';

const alignItems = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end'
};

const justifyContent = {
  fill: 'space-between',
  left: 'flex-start',
  right: 'flex-end'
};

const getStyles = lruMemoize((width: number, align: Align, marginRight: number) => ({
  flex: `${align === 'fill' ? 1 : 'none'} ${width}%`,
  marginRight: `${marginRight}px`
}));

const getContainerStyles = lruMemoize((align: Align, alignVertical: AlignVertical) => ({
  alignItems: `${alignItems[alignVertical]}`,
  justifyContent: `${justifyContent[align]}`
}));

interface StackProps {
  id?: string;
  align?: Align;
  alignVertical?: AlignVertical;
  widths?: number[];
  marginRight?: number;
  className?: string;
  isMyJioRedirection?: boolean;
  isStreetStoreChevron?: boolean;
  role?: string;
}

function Stack(props: StackProps): JSX.Element {
  const cx = classNames.bind(styles);
  const {
    id = '',
    children,
    widths,
    align = 'fill',
    alignVertical = 'center',
    marginRight = 0,
    className,
    isMyJioRedirection = false,
    isStreetStoreChevron = false,
    role
  } = props;
  return (
    <div
      id={id}
      className={cx('container', className)}
      style={getContainerStyles(align, alignVertical)}
      role={role}
    >
      {align === 'fill'
        ? Children.map(children, (child, index) => {
            let width = 0;
            if (widths) width = widths[index] || 0;
            let hideStyle = isMyJioRedirection && index === 0;
            let storeStyle = isStreetStoreChevron
              ? getStyles(width, 'left', marginRight)
              : getStyles(width, align, marginRight);
            if (child) {
              return (
                <div role="none" style={hideStyle ? {} : storeStyle}>
                  {child}
                </div>
              );
            }
            return null;
          })
        : children}
    </div>
  );
}

Stack.defaultProps = {
  align: 'fill',
  alignVertical: 'center',
  widths: [],
  marginRight: 0,
  className: ''
};

export default Stack;
